/**
 * Utility functions for auto-scrolling to slots in the personalisation flow.
 * Provides functionality to scroll to the next slot on the same step after selection.
 */

/**
 * Checks if auto-scroll should be enabled based on step conditions.
 * @param {Object} options - Configuration options
 * @param {Array} options.slots - Array of slot objects
 * @param {string} options.slotMode - Current slot mode ('FIXED' or 'SEQUENTIAL')
 * @param {boolean} options.showTabs - Whether tabs are shown
 * @returns {boolean} Whether auto-scroll should be enabled
 */
export function shouldEnableAutoScroll({ slots, slotMode, showTabs }) {
  return slotMode === 'FIXED' && !showTabs && slots.length > 1 && slots.every((slot) => slot.slotType === 'COMPONENT');
}

/**
 * Gets the next unselected slot after the current slot.
 * @param {Object} options - Configuration options
 * @param {Array} options.slots - Array of slot objects
 * @param {Array} options.selections - Current selections array
 * @param {string} options.currentSlotId - The ID of the slot that was just selected
 * @returns {Object|null} The next unselected slot or null if all are selected
 */
export function getNextUnselectedSlot({ slots, selections, currentSlotId }) {
  // Find the index of the current slot
  const currentSlotIndex = slots.findIndex((slot) => slot.id === currentSlotId);
  if (currentSlotIndex === -1) return null;

  // Look for the next unselected slot after the current one
  for (let i = currentSlotIndex + 1; i < slots.length; i++) {
    const slot = slots[i];
    const selection = selections.find((s) => s.id === slot.id);
    if (!selection?.selected) {
      return slot;
    }
  }

  // If no unselected slot found after current, return null
  return null;
}

/**
 * Scrolls to an element using the most React-friendly approach.
 * Uses scrollIntoView with block: 'start' for consistent behavior.
 * @param {HTMLElement} element - The element to scroll into view
 */
export function scrollToElement(element) {
  if (!element) return;

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest'
  });
}

/**
 * Scrolls to the next unselected slot after the current one.
 * @param {Object} options - Configuration options
 * @param {Array} options.slots - Array of slot objects
 * @param {Array} options.selections - Current selections array
 * @param {string} options.currentSlotId - The ID of the slot that was just selected
 * @param {Map} options.slotRefs - Map of slot refs
 * @returns {boolean} Whether scrolling was successful
 */
export function scrollToNextSlot({ slots, selections, currentSlotId, slotRefs }) {
  const nextSlot = getNextUnselectedSlot({ slots, selections, currentSlotId });
  if (!nextSlot) return false;

  const slotElement = slotRefs.get(nextSlot.id);
  if (slotElement) {
    scrollToElement(slotElement);
    return true;
  }

  return false;
}
